import { useCallback, useEffect, useMemo, useState } from 'react'
import { CostType, COST_TYPE_LABEL } from '../../lib/commission'
import { StandardCost } from '../../lib/standardCosts'
import {
  ChaseItem,
  StandardCostInput,
  chaseList,
  loadStandardCosts,
  reconcileArrival,
  saveStandardCost,
  setStandardCostActive,
} from '../../services/standardCostService'
import './StandardCostsView.css'
import { toast } from '../Toast'

/**
 * The rate card, and the invoices it is standing in for.
 *
 * Two panels because there are two jobs and they belong to different people on different
 * days. Somebody sets the standard inspection fee once a year. Somebody else works through
 * "what are we still waiting on" every Monday morning. Putting them on one screen is what
 * makes the second job explicable: the estimate on a vehicle came from a rate that is right
 * there, and can be corrected by the person looking at it.
 */

const COST_TYPES: CostType[] = ['Inspection', 'FreightIn', 'Reconditioning', 'FreightOut', 'OtherOutflow']

const aud = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const bandLabel = (r: StandardCost) => {
  if (r.basis !== 'distance_band') return 'Any distance'
  if (r.band_max_km === null || r.band_max_km === undefined) return `${r.band_min_km}km and over`
  return `${r.band_min_km}–${r.band_max_km}km`
}

const EMPTY: StandardCostInput = {
  cost_type: 'Inspection',
  label: '',
  basis: 'flat',
  band_min_km: null,
  band_max_km: null,
  amount: 0,
  active: true,
  notes: null,
}

export default function StandardCostsView() {
  const [rates, setRates] = useState<StandardCost[]>([])
  const [chases, setChases] = useState<ChaseItem[]>([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [form, setForm] = useState<StandardCostInput | null>(null)
  const [saving, setSaving] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const [r, c] = await Promise.all([loadStandardCosts(), chaseList()])
      setRates(r)
      setChases(c)
    } catch (e) {
      /* The most likely cause by far is that the migration has not been run, and saying so
         is more use than the raw PostgREST message. */
      setErr(
        (e as Error).message +
          ' — if this says the table does not exist, the standard costs migration has not been run yet.',
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const grouped = useMemo(() => {
    const out: { type: CostType; rows: StandardCost[] }[] = []
    for (const t of COST_TYPES) {
      const rows = rates.filter((r) => r.cost_type === t)
      if (rows.length) out.push({ type: t, rows })
    }
    return out
  }, [rates])

  const activeCount = rates.filter((r) => r.active).length

  async function save() {
    if (!form) return
    setSaving(true)
    setNote(null)
    try {
      await saveStandardCost(form)
      setForm(null)
      await refresh()
      toast.saved()
    } catch (e) {
      setNote((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  async function toggle(r: StandardCost) {
    try {
      await setStandardCostActive(r.id, !r.active)
      await refresh()
    } catch (e) {
      setNote((e as Error).message)
    }
  }

  async function reconcileOne(item: ChaseItem, actual: number) {
    setNote(null)
    try {
      const res = await reconcileArrival(item.expenseId, actual)
      setNote(res.message)
      await refresh()
    } catch (e) {
      setNote((e as Error).message)
    }
  }

  return (
    <div className="std-page">
      <div className="std-head">
        <h1>Standard costs</h1>
        <p>
          Standard rates for direct costs, used to estimate commission when a bill hasn’t arrived
          yet. No rate here means no estimate — not a guess nobody chose.
        </p>
      </div>

      {err && <div className="std-banner bad">{err}</div>}
      {note && <div className="std-banner">{note}</div>}

      <div className="std-panel">
        <div className="std-panel-head">
          <h2>The rate card</h2>
          <button className="std-btn primary" onClick={() => setForm({ ...EMPTY })}>
            Add a rate
          </button>
        </div>

        {loading ? (
          <div className="std-empty">Loading…</div>
        ) : rates.length === 0 ? (
          <div className="std-empty">
            <b>No standard rates are set.</b>
            <div>
              Until a rate exists here, missing bills stay missing — they are reported and chased,
              but nothing is accrued against a vehicle. Add the standard inspection fee and the
              freight rates by distance band to turn accruals on.
            </div>
          </div>
        ) : (
          grouped.map((g) => (
            <div key={g.type} className="std-group">
              <div className="std-group-t">{COST_TYPE_LABEL[g.type] || g.type}</div>
              <table className="std-table">
                <thead>
                  <tr>
                    <th>Rate</th>
                    <th>Applies to</th>
                    <th className="num">Amount</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.id} className={r.active ? '' : 'std-inactive'}>
                      <td>{r.label}</td>
                      <td>{bandLabel(r)}</td>
                      <td className="num">{aud(r.amount)}</td>
                      <td>
                        <span className={`std-pill ${r.active ? 'ok' : 'off'}`}>
                          {r.active ? 'Active' : 'Retired'}
                        </span>
                      </td>
                      <td className="std-actions">
                        <button
                          className="std-btn"
                          onClick={() =>
                            setForm({
                              id: r.id,
                              cost_type: r.cost_type,
                              label: r.label,
                              basis: r.basis,
                              band_min_km: r.band_min_km,
                              band_max_km: r.band_max_km,
                              amount: r.amount,
                              active: r.active,
                            })
                          }
                        >
                          Edit
                        </button>
                        <button className="std-btn" onClick={() => void toggle(r)}>
                          {r.active ? 'Retire' : 'Reinstate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        )}

        {rates.length > 0 && (
          <div className="std-foot">
            {activeCount} active rate{activeCount === 1 ? '' : 's'}. Retired rates are kept, never
            deleted — an accrual made last month has to stay explainable.
          </div>
        )}
      </div>

      <div className="std-panel">
        <div className="std-panel-head">
          <h2>Waiting on invoices</h2>
          <span className="std-count">{chases.length}</span>
        </div>
        {chases.length === 0 ? (
          <div className="std-empty">Nothing is sitting on an estimate.</div>
        ) : (
          <table className="std-table">
            <thead>
              <tr>
                <th>Cost</th>
                <th>Supplier</th>
                <th className="num">Accrued</th>
                <th>Waiting</th>
                <th>Invoice arrived at</th>
              </tr>
            </thead>
            <tbody>
              {chases.map((c) => (
                <ChaseRow key={c.expenseId} item={c} onReconcile={reconcileOne} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {form && (
        <div className="std-modal-wrap" role="dialog" aria-modal="true">
          <div className="std-modal">
            <h3>{form.id ? 'Edit rate' : 'Add a rate'}</h3>

            <label>
              <span>Cost</span>
              <select
                value={form.cost_type}
                onChange={(e) => setForm({ ...form, cost_type: e.target.value as CostType })}
              >
                {COST_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {COST_TYPE_LABEL[t] || t}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Name</span>
              <input
                value={form.label}
                placeholder="Standard inspection fee"
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </label>

            <label>
              <span>Priced by</span>
              <select
                value={form.basis}
                onChange={(e) =>
                  setForm({
                    ...form,
                    basis: e.target.value as 'flat' | 'distance_band',
                    band_min_km: e.target.value === 'distance_band' ? (form.band_min_km ?? 0) : null,
                    band_max_km: e.target.value === 'distance_band' ? form.band_max_km ?? null : null,
                  })
                }
              >
                <option value="flat">A flat amount</option>
                <option value="distance_band">Distance band</option>
              </select>
            </label>

            {form.basis === 'distance_band' && (
              <div className="std-row">
                <label>
                  <span>From (km)</span>
                  <input
                    type="number"
                    min={0}
                    value={form.band_min_km ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, band_min_km: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </label>
                <label>
                  <span>To (km)</span>
                  <input
                    type="number"
                    min={0}
                    placeholder="leave blank for no limit"
                    value={form.band_max_km ?? ''}
                    onChange={(e) =>
                      setForm({ ...form, band_max_km: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </label>
              </div>
            )}

            <label>
              <span>Amount (GST inclusive)</span>
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              />
            </label>

            <p className="std-hint">
              Bands run from their start up to but not including their end, so 0–100 and 100–500 meet
              at 100 without both claiming it. Leave the top band open-ended.
            </p>

            <div className="std-modal-actions">
              <button className="std-btn" onClick={() => setForm(null)} disabled={saving}>
                Cancel
              </button>
              <button className="std-btn primary" onClick={() => void save()} disabled={saving}>
                {saving ? 'Saving…' : 'Save rate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ChaseRow({
  item,
  onReconcile,
}: {
  item: ChaseItem
  onReconcile: (item: ChaseItem, actual: number) => Promise<void>
}) {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const n = Number(value)
  const ready = value.trim() !== '' && Number.isFinite(n) && n >= 0

  return (
    <tr>
      <td>{COST_TYPE_LABEL[item.costType as CostType] || item.costType || '—'}</td>
      <td>{item.supplier || <em>not recorded</em>}</td>
      <td className="num">{aud(item.estimatedAmount)}</td>
      <td>
        <span className={`std-pill ${(item.daysWaiting || 0) > 14 ? 'bad' : 'warn'}`}>
          {item.daysWaiting === null ? 'unknown' : `${item.daysWaiting}d`}
        </span>
      </td>
      <td className="std-recon">
        <input
          type="number"
          min={0}
          step="0.01"
          placeholder="actual amount"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          className="std-btn primary"
          disabled={!ready || busy}
          onClick={async () => {
            setBusy(true)
            try {
              await onReconcile(item, n)
            } finally {
              setBusy(false)
            }
          }}
        >
          {busy ? '…' : 'Reconcile'}
        </button>
      </td>
    </tr>
  )
}
